import { connect } from "react-redux";
import { useIntl } from "react-intl";
import { Field, useFormState, useForm } from "react-final-form";
import { useEffect } from "react";
import Form from "../../../common/form";
import Section from "../../../common/section";
import TextInputField from "../../../common/fields/TextInput";
import DropdownInput from "../../../common/fields/DropdownInput";
import SwitchInput from "../../../common/fields/SwitchInput";
import * as Validations from "../../../common/form/validator";
import { IntegrationConstants } from "../../../../constants";
import {
  integrationCreateRequest,
  integrationUpdateRequest,
} from "../../../../store/slices/integrationSlice";
import { Message } from "primereact/message";
const IntegrationForm = ({
  integration,
  onCancel,
  requesting,
  integrationCreateRequest,
  integrationUpdateRequest,
  title,
  disabled = false,
  integrations = {},
}) => {
  const intl = useIntl();

  // Integration type options
  // const integrationTypes = [
  //   {
  //     name: intl.formatMessage({ id: "analytics" }),
  //     value: IntegrationConstants.INTEGRATION_TYPES.ANALYTICS,
  //   },
  // ];
  const integrationTypes = [
  {
    name: intl.formatMessage({ id: "analytics" }),
    value: IntegrationConstants.INTEGRATION_TYPES.ANALYTICS,
  },
  {
    name: intl.formatMessage({ id: "content_generation" }),
    value: IntegrationConstants.INTEGRATION_TYPES.CONTENT_GENERATION,
  },
];


  // Integration sub-type options based on type
  const getSubTypeOptions = (type) => {
    switch (type) {
      case IntegrationConstants.INTEGRATION_TYPES.ANALYTICS:
        return [
          {
            name: intl.formatMessage({ id: "google_analytics" }),
            value: IntegrationConstants.ANALYTICS_PROVIDERS.GOOGLE_ANALYTICS,
          },
        ];
         case IntegrationConstants.INTEGRATION_TYPES.CONTENT_GENERATION:
      return [
        {
          name: intl.formatMessage({ id: "google_gemini" }),
          value: IntegrationConstants.CONTENT_GENERATION_PROVIDERS.GEMINI,
        },
      ];
      default:
        return [];
    }
  };

  // Component that uses useFormState to access current form values
  const SubTypeField = () => {
    const formState = useFormState();
    const form = useForm();
    const currentType = formState.values.integrationType;
    const currentSubType = formState.values.integrationSubType;
    const subTypeOptions = getSubTypeOptions(currentType);

    // Reset sub-type when integration type changes and current sub-type is invalid
    useEffect(() => {
      if (currentSubType && subTypeOptions.length > 0) {
        const isCurrentSubTypeValid = subTypeOptions.some(
          (option) => option.value === currentSubType
        );

        if (!isCurrentSubTypeValid) {
          form.change("integrationSubType", "");
        }
      }
    }, [currentType, currentSubType, subTypeOptions, form]);

    return (
      <Field
        name="integrationSubType"
        validate={Validations.composeValidators(Validations.required)}
      >
        {({ input, meta }) => (
          <DropdownInput
            input={input}
            meta={meta}
            label={intl.formatMessage({
              id: "integration_sub_type",
            })}
            options={subTypeOptions}
            placeholder={intl.formatMessage({
              id: "integration_sub_type",
            })}
            required={true}
            disabled={disabled}
            readonly={!!integration}
          />
        )}
      </Field>
    );
  };

  const onSubmit = (values) => {
    // Build configuration object from individual fields
    const configuration = {};
    if (values.configuration) {
      Object.keys(values.configuration).forEach((key) => {
        if (values.configuration[key]) {
          configuration[key] = values.configuration[key];
        }
      });
    }

    const formData = {
      name: values.name,
      type:
        values.integrationType ||
        IntegrationConstants.INTEGRATION_TYPES.ANALYTICS,
      subType: values.integrationSubType,
      isEnabled: values.isActive || false,
      configuration: configuration,
    };

    if (integration) {
      integrationUpdateRequest({ ...formData, id: integration.id });
    } else {
      integrationCreateRequest(formData);
    }
  };

  const initialValues = {
    name: integration?.name || "",
    integrationType: integration?.type || "",
    integrationSubType: integration?.subType || "",
    isActive:
      integration?.isEnabled !== undefined ? integration.isEnabled : true,
    configuration: integration?.configuration || {},
  };

  return (
    <Form
      initialValues={initialValues}
      disabled={disabled}
      submitBtn={{
        label: integration
          ? intl.formatMessage({ id: "update_integration" })
          : intl.formatMessage({ id: "add_integration" }),
        onClick: onSubmit,
      }}
      cancelBtn={{
        label: intl.formatMessage({ id: "cancel" }),
        onClick: onCancel,
      }}
    >
      <div className="flex flex-column gap-4">
        <Section title="integration_details">
          <div className="grid">
            <div className="col-12 md:col-6 lg:col-4">
              <Field
                name="name"
                component={TextInputField}
                label={intl.formatMessage({ id: "integration_name" })}
                required
                placeholder={intl.formatMessage({ id: "integration_name" })}
                disabled={disabled}
                validate={Validations.composeValidators(
                  Validations.required,
                  Validations.minLength(2),
                  Validations.maxLength(100)
                )}
              />
            </div>

            <div className="col-12 md:col-6 lg:col-4">
              <Field
                name="integrationType"
                validate={Validations.composeValidators(Validations.required)}
              >
                {({ input, meta }) => (
                  <DropdownInput
                    input={input}
                    meta={meta}
                    label={intl.formatMessage({ id: "integration_type" })}
                    options={integrationTypes}
                    placeholder={intl.formatMessage({ id: "integration_type" })}
                    required={true}
                    disabled={disabled}
                    readonly={!!integration}
                  />
                )}
              </Field>
            </div>

            <div className="col-12 md:col-6 lg:col-4">
              <SubTypeField />
            </div>

            <div className="col-12 md:col-6 lg:col-4">
              <Field
                name="isActive"
                component={SwitchInput}
                type="checkbox"
                label={intl.formatMessage({ id: "integration_active" })}
                disabled={disabled}
              />
            </div>
          </div>
        </Section>

        <Field name="integrationSubType">
          {({ input }) => {
            if (
              input.value ===
              IntegrationConstants.ANALYTICS_PROVIDERS.GOOGLE_ANALYTICS
            ) {
              return (
                <Section title="google_analytics_configuration">
                  <div className="grid">
                    <div className="col-12">
                      <Field
                        name={`configuration.${IntegrationConstants.GOOGLE_ANALYTICS_FIELDS.MEASUREMENT_ID}`}
                        component={TextInputField}
                        label={intl.formatMessage({ id: "measurement_id" })}
                        placeholder="G-XXXXXXXXXX"
                        required
                        disabled={disabled}
                        validate={Validations.composeValidators(
                          Validations.required,
                          (value) => {
                            // Google Analytics measurement ID pattern: G-XXXXXXXXXX
                            const gaPattern = /^G-[A-Z0-9]{10}$/;
                            if (value && !gaPattern.test(value)) {
                              return intl.formatMessage({
                                id: "invalid_measurement_id_format",
                              });
                            }
                            return undefined;
                          }
                        )}
                        helpText={intl.formatMessage({
                          id: "measurement_id_help",
                        })}
                      />
                    </div>
                  </div>
                </Section>
              );
            }

            if (input.value === IntegrationConstants.CONTENT_GENERATION_PROVIDERS.GEMINI) {
              return (
                <Section title="google_gemini_configuration">
                  <div className="grid">
                    {/* API Key */}
                    <div className="col-12">
                      <Field
                        name={`configuration.${IntegrationConstants.GEMINI_FIELDS.API_KEY}`}
                        component={TextInputField}
                        label={intl.formatMessage({ id: "api_key" })}
                        placeholder="Enter your Google Gemini API key"
                        required
                        disabled={disabled}
                        validate={Validations.required}
                      />
                    </div>
                  </div>
                  <div className="mb-2 mt-3">
                      <Message
                        severity="warn"
                        style={{ width: '100%' }}
                        text={intl.formatMessage({ id: "googleApiWarning" })}
                      />
                    </div>
                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
                    <ol className="pl-5 space-y-2" style={{ lineHeight: '1.6' }}>
                      <li>{intl.formatMessage({ id: "googleApiStep1" })}</li>
                      <li>{intl.formatMessage({ id: "googleApiStep2" })}</li>
                      <li>{intl.formatMessage({ id: "googleApiStep3" }, { domain: window.location.hostname })}</li>
                      <li>{intl.formatMessage({ id: "googleApiStep4" })}</li>
                      <li>{intl.formatMessage({ id: "googleApiStep5" })}</li>
                    </ol>
                  </div>
                </Section>
              );
            }
            return undefined;
          }}
        </Field>
      </div>
    </Form>
  );
};

const mapStateToProps = (state) => ({
  requesting: state.integrations.requesting,
  integrations: state.integrations.integrations,
});

const mapDispatchToProps = {
  integrationCreateRequest,
  integrationUpdateRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(IntegrationForm);
